<script lang="ts">
    import {onDestroy, onMount} from "svelte";
    import type {IBalance} from "$lib/interfaces/IBalance";
    import type {IUser} from "$lib/interfaces/IUserMe";
    import NumberFormat from "$lib/components/NumberFormat.svelte";
    import {userService} from "$lib/services/user.service";
    import {balances} from "$lib/stores";
    import {page} from "$app/stores";
    import {environment} from "$lib/env";
    import {getCookie} from "$lib/utils/cookies";
    import {toasts} from "$lib/stores/toastStores";
    import {openDialog} from "$lib/stores/dialogStores";
    import {translate} from "$lib/i18n";
    import {goto} from "$app/navigation";
    import {sortCryptoCurrencies} from "$lib/utils/currency-utils";
    import {get} from "svelte/store";


    interface Props {
        localUser: IUser;
    }

    let { localUser = $bindable() }: Props = $props();
    let activeCurrency: IBalance | undefined = $state();
    let isOpen = $state(false);
    let totalBalance: number = $state(0);
    let realMoney: IBalance[] = $state([]);
    let cryptos: IBalance[] = $state([]);
    let cashierRef: HTMLDivElement = $state();
    let buttonRef: HTMLButtonElement = $state();
    let loading: boolean = $state(false);
    let currencyChangeInProgress: boolean = $state(false);
    let isMobile: boolean = $state(false);
    let isExtraSmall: boolean = $state(false);

    // Check if device is mobile or extra small
    function checkScreenSize() {
        isMobile = window.innerWidth < 768;
        isExtraSmall = window.innerWidth < 380;
    }

    function combineUSDTBalances(balances: IBalance[]): IBalance[] {
        const usdtVariants = new Set(['USDT', 'USDTE', 'USDTT']);
        let usdtBalances = balances.filter(b => usdtVariants.has(b.shortName));

        if (usdtBalances.length === 0) return balances;

        // Find the main USDT or use the first variant if USDT doesn't exist
        let mainUSDT = usdtBalances.find(b => b.shortName === 'USDT') || usdtBalances[0];

        // Create a combined USDT balance
        let combinedUSDT: IBalance = {
            ...mainUSDT,
            balance: usdtBalances.reduce((sum, curr) => sum + (curr.balance || 0), 0),
            fiatEquivalent: usdtBalances.reduce((sum, curr) => sum + (curr.fiatEquivalent || 0), 0),
            name: 'USDT',
            shortName: 'USDT',
        };

        // Return all non-USDT balances plus the combined USDT balance
        return balances.filter(b => !usdtVariants.has(b.shortName)).concat(combinedUSDT);
    }


    async function creditCardDeposit(isModal: boolean = false) {
        loading = true;
        try {
            const response = await fetch(`${environment.apiUrl}/credit-card/init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getCookie('forza_token')}`
                },
                body: JSON.stringify({
                    currency: 'EUR'
                })
            }).then((res) => res.json()).then((res) => {
                if (res.status === 400) {
                    loading = false;
                    toasts.send(res.message, 'error');
                    openDialog('MiniKYCModal');
                    return;
                }
                loading = false;
                if (isModal) {
                    openDialog('PaymentIqIframe', res.data);
                    return;
                }
            });
        } catch (err: any) {
            loading = false;
        }
    }

    function openCashier() {
        isOpen = !isOpen;
    }

    balances.subscribe((value: IBalance[] | null) => {
        if (!value) return;

        // Combine USDT balances before processing
        const processedBalances = combineUSDTBalances(value);

        // Fix: Calculate total balance correctly for both fiat and crypto currencies
        totalBalance = processedBalances.reduce((acc: number, balance: IBalance) => {
            if (balance.isCrypto) {
                // For crypto, use fiatEquivalent
                return acc + (balance.fiatEquivalent || 0);
            } else {
                // For fiat currencies, use balance directly
                return acc + (balance.balance || 0);
            }
        }, 0);

        // Find active currency, considering combined USDT
        if (localUser.activeCryptoCurrency === 'USDTE' || localUser.activeCryptoCurrency === 'USDTT') {
            activeCurrency = processedBalances.find((balance: IBalance) => balance.name === 'USDT');
        } else {
            activeCurrency = processedBalances.find((balance: IBalance) => balance.name === localUser.activeCryptoCurrency);
        }

        realMoney = processedBalances.filter((balance: IBalance) => !balance.isCrypto);

        const filteredCryptos = processedBalances.filter((balance: IBalance) => balance.isCrypto);
        cryptos = sortCryptoCurrencies(filteredCryptos);

    });

    function handleClickOutside(event: MouseEvent) {
        if (cashierRef && !cashierRef.contains(event.target as Node)) {
            isOpen = false;
        }
    }

    function getBalances() {
        userService.getBalances().then((response: any) => {
            if (response) {
                return response.data;
            }
            throw new Error('Failed to fetch balances');
        }).then(data => {
            balances.set(data.data);
        }).catch(err => {
            console.error(err);
        });
    }

    onMount(() => {
        getBalances();
        document.addEventListener('click', handleClickOutside);
        window.addEventListener('resize', checkScreenSize);
        checkScreenSize();

        return () => {
            if (typeof document !== "undefined") {
                document.removeEventListener("click", handleClickOutside);
                window.removeEventListener('resize', checkScreenSize);
            }
        };
    });

    function refreshGameWithNewCurrency() {
        // Check if we're on a game page
        if ($page.url.pathname.includes('/game')) {
            // Show a loading indicator or toast
            toasts.send(get(translate)('Updating currency...'), 'info');

            // Close the cashier dropdown
            isOpen = false;

            // Get the current URL and parameters
            const currentUrl = $page.url.pathname;
            const searchParams = $page.url.searchParams;

            // Reload the current page to refresh the game with the new currency
            // This will force the game provider to recognize the currency change
            setTimeout(() => {
                // Navigate to the same URL which will trigger a refresh
                goto(currentUrl + (searchParams.toString() ? `?${searchParams.toString()}` : ''), {
                    replaceState: false,
                    invalidateAll: true
                });
            }, 500);
        }
    }

    function setCurrency(currency: IBalance) {
        if (currencyChangeInProgress) return;

        currencyChangeInProgress = true;

        // For USDT, determine which variant to use based on the selected network
        let currencyName = currency.name;

        if (currency.name === 'USDT') {
            // Get the original balances to find the network-specific variants
            const usdtBalances = get(balances)?.filter(b => ['USDTE', 'USDTT'].includes(b.shortName)) || [];

            // Default to USDTE if available, otherwise use USDTE
            const usdteBalance = usdtBalances.find(b => b.shortName === 'USDTE');
            const usdttBalance = usdtBalances.find(b => b.shortName === 'USDTT');

            // If both variants exist, use the one with higher balance
            if (usdteBalance && usdttBalance) {
                currencyName = (usdteBalance.balance || 0) >= (usdttBalance.balance || 0) ? 'USDTE' : 'USDTT';
                console.log(`Selected USDT variant: ${currencyName} based on balance comparison`);
            } else if (usdteBalance) {
                currencyName = 'USDTE';
                console.log(`Selected USDT variant: USDTE (only variant available)`);
            } else if (usdttBalance) {
                currencyName = 'USDTT';
                console.log(`Selected USDT variant: USDTT (only variant available)`);
            }
        }

        // Only proceed if this is actually a change
        if (localUser.activeCryptoCurrency === currencyName) {
            currencyChangeInProgress = false;
            isOpen = false;
            return;
        }

        userService.setActiveCurrency(currencyName).then(() => {
            userService.getUser().then((response: any) => {
                if (response) {
                    return response.data;
                }
                getBalances();
                throw new Error('Failed to fetch user');
            }).then(data => {
                localUser = data.data;
                activeCurrency = currency;

                // Refresh the game if we're on a game page
                refreshGameWithNewCurrency();

                currencyChangeInProgress = false;
            }).catch(err => {
                console.error(err);
                currencyChangeInProgress = false;
            });
        }).catch(err => {
            console.error(err);
            currencyChangeInProgress = false;
        });
    }

    // Helper function to get currency icon
    function getCurrencyIcon(currencyName: string) {
        const lowerCaseName = currencyName.toLowerCase();
        return `/currencies/${lowerCaseName}.png`;
    }

    // Proper Svelte error handler for images
    function handleImageError(event: Event) {
        const imgElement = event.target as HTMLImageElement;
        imgElement.src = '/currencies/default.png';
    }
</script>

{#if activeCurrency}
    <div class="relative font-poppins" bind:this={cashierRef}>
        <!-- Main wallet button - Responsive design -->
        <button
                bind:this={buttonRef}
                class="flex items-center bg-primary-dark border border-primary-blue/20 hover:bg-primary-blue/10 transition-colors rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 gap-1 sm:gap-2"
                onclick={() => openCashier()}
                disabled={currencyChangeInProgress}
                aria-label="Open wallet"
                title="Wallet"
        >
            <!-- Currency icon -->
            <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-full overflow-hidden flex items-center justify-center bg-primary-blue/10">
                <img
                        src={getCurrencyIcon(activeCurrency.name) || "/placeholder.svg"}
                        alt={activeCurrency.name}
                        class="w-4 h-4 sm:w-6 sm:h-6"
                        onerror={handleImageError}
                />
            </div>

            <!-- Balance display - Hide currency name on very small screens -->
            <div class="flex flex-col items-start">
                <span class="text-[10px] sm:text-xs text-primary-lightBlue hidden xs:block">{activeCurrency.name}</span>
                <span class="text-xs sm:text-sm text-white font-medium">
        {#if $page.url.pathname.includes('/game')}
          {get(translate)('In-Game')}
        {:else}
          {activeCurrency.isCrypto ?
              `${localUser.defaultFiatCurrencySymbol} ${activeCurrency.fiatEquivalent?.toFixed(2)}` :
              `${activeCurrency.name === 'GBP' ? '£' : activeCurrency.name === 'EUR' ? '€' : '$'} ${activeCurrency.balance?.toFixed(2)}`
          }
        {/if}
      </span>
            </div>

            <!-- Arrow indicator -->
            <div class={`text-primary-lightBlue transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </button>

        {#if isOpen}
            <!-- Dropdown positioned relative to the button - Moved to the right on small screens -->
            <div
                    class="absolute bg-primary-dark w-80 rounded-xl shadow-xl border border-amber-50/20 z-50"
                    style="top: calc(100% + 8px); right: {isMobile ? '-80px' : '0'}; max-height: 85vh;"
            >
                <!-- Header with total balance -->
                <div class="p-3 sm:p-4 border-b border-primary-blue/20 flex items-center justify-between">
                    <h3 class="text-sm sm:text-base text-white font-medium">{get(translate)('Total Balance')}</h3>
                    <div class="flex flex-col items-end">
          <span class="text-xs sm:text-sm text-white font-medium">
            {localUser.defaultFiatCurrencySymbol}
              <NumberFormat value={totalBalance} digitsInfo="1.2-2" locale="en-US"></NumberFormat>
          </span>
                    </div>
                </div>

                <!-- Wallet content -->
                <div class="overflow-y-auto" style="max-height: calc(85vh - 120px);">
                    <!-- Real Money section -->
                    {#if realMoney.length > 0}
                        <div class="p-3 sm:p-4 bg-primary-blue/5">
                            <h4 class="text-primary-lightBlue text-[10px] sm:text-xs uppercase font-medium mb-2 sm:mb-3">{get(translate)('Real Money')}</h4>
                            <div class="space-y-2">
                                {#each realMoney as currency}
                                    <div
                                            class={`flex items-center justify-between p-2 sm:p-3 rounded-lg cursor-pointer transition-colors border border-amber-50/20 ${activeCurrency?.name === currency.name ? 'bg-primary-fire/10 border border-primary-fire/30' : 'bg-primary-dark border-amber-50/20 hover:bg-primary-blue/10'}`}
                                            onclick={() => setCurrency(currency)}
                                    >
                                        <!-- Currency info - More compact on mobile -->
                                        <div class="flex items-center gap-2 sm:gap-3">
                                            <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center bg-primary-blue/10">
                                                <img
                                                        src={getCurrencyIcon(currency.name) || "/placeholder.svg"}
                                                        alt={currency.name}
                                                        class="w-5 h-5 sm:w-7 sm:h-7"
                                                        onerror={handleImageError}
                                                />
                                            </div>
                                            <div>
                                                <div class="text-sm sm:text-base text-white font-medium">{currency.name}</div>
                                                <div class="text-[10px] sm:text-xs text-primary-lightBlue">
                                                    {currency.name === 'GBP' ? '£' : currency.name === 'EUR' ? '€' : '$'}
                                                    <NumberFormat value={currency.balance} digitsInfo="1.2-2" locale="en-US"></NumberFormat>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Active indicator -->
                                        {#if activeCurrency?.name === currency.name}
                                            <div class="text-primary-fire">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/if}

                    <!-- Crypto section -->
                    {#if cryptos.length > 0}
                        <div class="p-3 sm:p-4">
                            <h4 class="text-primary-lightBlue text-[10px] sm:text-xs uppercase font-medium mb-2 sm:mb-3">{get(translate)('Crypto')}</h4>
                            <div class="space-y-2">
                                {#each cryptos as currency}
                                    <div
                                            class={`flex items-center justify-between p-2 sm:p-3 rounded-lg cursor-pointer transition-colors border border-amber-50/20 ${activeCurrency?.name === currency.name ? 'bg-primary-fire/10 border border-primary-fire/30' : 'bg-primary-dark border-amber-50/20 hover:bg-primary-blue/10'}`}
                                            onclick={() => setCurrency(currency)}
                                    >
                                        <!-- Currency info - More compact on mobile -->
                                        <div class="flex items-center gap-2 sm:gap-3">
                                            <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center bg-primary-blue/10">
                                                <img
                                                        src={getCurrencyIcon(currency.name) || "/placeholder.svg"}
                                                        alt={currency.name}
                                                        class="w-5 h-5 sm:w-7 sm:h-7"
                                                        onerror={handleImageError}
                                                />
                                            </div>
                                            <div>
                                                <div class="text-sm sm:text-base text-white font-medium">{currency.name}</div>
                                                <div class="flex flex-col">
                        <span class="text-[10px] sm:text-xs text-primary-lightBlue truncate" style="max-width: 180px;">
                          {currency.balance?.toFixed(isExtraSmall ? 2 : 4)} {currency.name}
                        </span>
                                                    <span class="text-[10px] sm:text-xs text-primary-lightBlue/70">
                          ≈ {localUser.defaultFiatCurrencySymbol} {currency.fiatEquivalent?.toFixed(2)}
                        </span>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Active indicator -->
                                        {#if activeCurrency?.name === currency.name}
                                            <div class="text-primary-fire">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/if}
                </div>


                <!-- Footer with deposit button -->
                <div class="p-3 sm:p-4 border-t border-primary-blue/20 sticky bottom-0 bg-primary-dark">
                    <button
                            class="w-full py-2.5 sm:py-3 px-4 rounded-full bg-gradient-to-r from-primary-fire to-yellow-400 text-white text-sm sm:text-base font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            onclick={() => openDialog('DepositModal')}
                            disabled={localUser.selfExcluded || currencyChangeInProgress || loading}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <span>{get(translate)('Deposit')}</span>
                    </button>
                </div>
            </div>
        {/if}
    </div>
{/if}

<style>
    /* Add custom breakpoint for extra small screens */
    @media (min-width: 480px) {
        .xs\:block {
            display: block;
        }
    }

    /* Improve scrollbar appearance */
    div::-webkit-scrollbar {
        width: 6px;
    }

    div::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
    }

    div::-webkit-scrollbar-thumb {
        background: rgba(255, 62, 0, 0.5);
        border-radius: 3px;
    }

    div::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 62, 0, 0.7);
    }
</style>